import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ImagePlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { projects } from "../data/projects";
import { Reveal, Eyebrow } from "../components/Reveal";
import Contact from "../components/Contact";
import Footer from "../components/Footer";

const SectionHeading = ({ eyebrow, children }) => (
    <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{children}</h2>
    </Reveal>
);

const ImagePlaceholder = ({ label, note = "visuals coming soon ✦", className = "" }) => (
    <div className={`relative flex flex-col items-center justify-center overflow-hidden rounded-[2rem] bg-gradient-to-br from-lav-50 via-lav-100 to-lav-200 ring-1 ring-ink/5 ${className}`}>
        <div
            className="absolute inset-0 bg-dots opacity-30"
            style={{
                maskImage: "radial-gradient(ellipse at 50% 50%, black 30%, transparent 75%)",
                WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, black 30%, transparent 75%)",
            }}
        />
        <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/70 ring-1 ring-white shadow-[0_10px_30px_rgba(112,71,245,0.15)] backdrop-blur-sm">
            <ImagePlus className="h-5 w-5 text-viol/70" />
        </span>
        {label && <p className="relative mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-mist/80">{label}</p>}
        <p className="hand relative mt-1 text-lg text-viol/80">{note}</p>
    </div>
);

const ProjectImage = ({ src, caption, delay = 0 }) => (
    <Reveal delay={delay}>
        <figure>
            <div className="group overflow-hidden rounded-[2rem] ring-1 ring-ink/5 shadow-[0_24px_70px_rgba(112,71,245,0.15)]">
                <img
                    src={src}
                    alt={caption || "Project visual"}
                    loading="lazy"
                    className="h-auto w-full transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                />
            </div>
            {caption && (
                <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mist/80">
                    {caption}
                </figcaption>
            )}
        </figure>
    </Reveal>
);

const TextPlaceholder = ({ note = "content coming soon ✦" }) => (
    <p className="hand text-lg text-viol/70">{note}</p>
);

const Subheading = ({ children }) => (
    <p className="mt-3 text-lg font-bold text-ink/70 sm:text-xl">{children}</p>
);

const BulletList = ({ label, items }) => (
    <div className="mt-8">
        {label && <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-viol">{label}</p>}
        <ul className="mt-3 space-y-2.5">
            {items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-base leading-relaxed text-ink/80">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sun" />
                    {it}
                </li>
            ))}
        </ul>
    </div>
);

const NumberedItems = ({ items }) => (
    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
        {items.map((it) => (
            <div key={it.num} className="rounded-[1.75rem] bg-white/60 p-6 ring-1 ring-ink/5">
                <span className="font-mono text-xs font-bold text-viol">{it.num}</span>
                <h4 className="mt-2 text-base font-bold text-ink">{it.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-mist">{it.desc}</p>
            </div>
        ))}
    </div>
);

const QuoteCallout = ({ text, label }) => (
    <div className="glass mt-8 rounded-[1.75rem] p-6 sm:p-8">
        {label && <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-viol">{label}</p>}
        <p className="mt-3 text-xl font-semibold italic leading-snug text-ink/90 sm:text-2xl">“{text}”</p>
    </div>
);

const SectionWrapper = ({ decorated, flip, testid, children }) => {
    if (!decorated) {
        return (
            <section className="relative mx-auto max-w-6xl px-4 py-20 sm:px-8 lg:py-28" data-testid={testid}>
                {children}
            </section>
        );
    }
    return (
        <section className="relative overflow-hidden py-20 lg:py-28" data-testid={testid}>
            <div
                className={`pointer-events-none absolute top-1/3 h-[26rem] w-[26rem] rounded-full blur-3xl ${
                    flip ? "-right-48 bg-viol/10" : "-left-48 bg-lav-200 opacity-70"
                }`}
            />
            <div className="relative mx-auto max-w-6xl px-4 sm:px-8">{children}</div>
        </section>
    );
};

const SectionRenderer = ({ section, index }) => {
    const { id, type, eyebrow, heading, decorated } = section;
    const flip = index % 2 === 1;
    const testid = `project-${id}`;

    if (type === "text") {
        return (
            <SectionWrapper decorated={decorated} flip={flip} testid={testid}>
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <SectionHeading eyebrow={eyebrow}>{heading}</SectionHeading>
                        {section.subheading && <Subheading>{section.subheading}</Subheading>}
                    </div>
                    <Reveal delay={0.1} className="lg:col-span-8">
                        {section.copy ? (
                            <>
                                <p className="text-lg leading-relaxed text-ink/85 sm:text-xl sm:leading-[1.7]">{section.copy}</p>
                                <span className="mt-8 block h-1 w-16 rounded-full bg-sun" aria-hidden="true" />
                            </>
                        ) : (!section.items || section.items.length === 0) ? (
                            <TextPlaceholder />
                        ) : null}
                        {section.extraParagraphs && section.extraParagraphs.length > 0 && (
                            <div className="mt-6 space-y-5">
                                {section.extraParagraphs.map((p, i) => (
                                    <p key={i} className="text-lg leading-relaxed text-ink/85 sm:text-xl sm:leading-[1.7]">{p}</p>
                                ))}
                            </div>
                        )}
                        {section.items && section.items.length > 0 && <NumberedItems items={section.items} />}
                        {section.quote && <QuoteCallout text={section.quote} label={section.quoteLabel} />}

                        {section.products && section.products.length > 0 && (
                            <div className="mt-10 border-t border-lav-300/60 pt-10">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">Products worked on</p>
                                <div className="mt-6 space-y-6">
                                    {section.products.map((p) => (
                                        <div key={p.name} className="border-b border-lav-300/40 pb-6 last:border-0 last:pb-0">
                                            <div className="flex flex-wrap items-baseline gap-3">
                                                <h4 className="text-base font-bold text-ink">{p.name}</h4>
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-mist">{p.location}</span>
                                            </div>
                                            <p className="mt-2 text-sm leading-relaxed text-ink/70">{p.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {section.roleLabel && (
                            <div className="mt-10 border-t border-lav-300/60 pt-10">
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">{section.roleLabel}</p>
                                {section.roleCopy  && <p className="mt-5 text-lg leading-relaxed text-ink/85">{section.roleCopy}</p>}
                                {section.roleCopy2 && <p className="mt-4 text-lg leading-relaxed text-ink/85">{section.roleCopy2}</p>}
                                {section.roleCopy3 && <p className="mt-4 text-lg leading-relaxed text-ink/85">{section.roleCopy3}</p>}
                                {section.roleList && section.roleList.length > 0 && (
                                    <div className="mt-6 max-w-2xl">
                                        <BulletList label="I also worked on" items={section.roleList} />
                                    </div>
                                )}
                            </div>
                        )}

                        {section.ctas && section.ctas.length > 0 && (
                            <div className="mt-10 flex flex-wrap gap-3">
                                {section.ctas.map((cta) => (
                                    <a
                                        key={cta.label}
                                        href={cta.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 rounded-full border border-viol/25 bg-white/70 px-5 py-2.5 text-sm font-semibold text-viol backdrop-blur-sm transition-all duration-300 hover:border-viol hover:bg-viol hover:text-white hover:shadow-[0_8px_30px_rgba(112,71,245,0.25)] active:scale-95"
                                    >
                                        {cta.label}
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                ))}
                            </div>
                        )}
                    </Reveal>
                </div>
            </SectionWrapper>
        );
    }

    if (type === "text-images") {
        return (
            <SectionWrapper decorated={decorated} flip={flip} testid={testid}>
                <SectionHeading eyebrow={eyebrow}>{heading}</SectionHeading>
                {section.subheading && <Subheading>{section.subheading}</Subheading>}
                <Reveal delay={0.1} className="mt-6">
                    {section.copy ? (
                        <p className="max-w-3xl text-lg leading-relaxed text-ink/85">{section.copy}</p>
                    ) : (
                        <TextPlaceholder />
                    )}
                    {section.extraParagraphs && section.extraParagraphs.length > 0 && (
                        <div className="mt-5 max-w-3xl space-y-5">
                            {section.extraParagraphs.map((p, i) => (
                                <p key={i} className="text-lg leading-relaxed text-ink/85">{p}</p>
                            ))}
                        </div>
                    )}
                    {section.list && section.list.length > 0 && (
                        <div className="max-w-2xl">
                            <BulletList label={section.listLabel} items={section.list} />
                        </div>
                    )}
                    {section.quote && (
                        <div className="max-w-2xl">
                            <QuoteCallout text={section.quote} label={section.quoteLabel} />
                        </div>
                    )}
                    {section.blocks && section.blocks.length > 0 && (
                        <div className="mt-8 max-w-3xl space-y-7">
                            {section.blocks.map((b) => (
                                <div key={b.label}>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-viol">{b.label}</p>
                                    {b.text && <p className="mt-2 text-base leading-relaxed text-ink/80 sm:text-lg">{b.text}</p>}
                                    {b.items && b.items.length > 0 && (
                                        <ul className="mt-3 space-y-2">
                                            {b.items.map((it) => (
                                                <li key={it} className="flex items-start gap-3 text-base leading-relaxed text-ink/80">
                                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sun" />
                                                    {it}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Reveal>
                {section.images.length > 0 && (
                    <div className={`mt-10 ${section.images.length > 1 ? "grid grid-cols-1 gap-8 sm:grid-cols-2" : ""}`}>
                        {section.images.map((img, i) => (
                            <ProjectImage key={img.src} src={img.src} caption={img.caption} delay={(i % 2) * 0.1} />
                        ))}
                    </div>
                )}
                {section.secondBlock && (
                    <div className="mt-16 border-t border-lav-300/60 pt-16">
                        {section.secondBlock.label && (
                            <Reveal>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-viol">{section.secondBlock.label}</p>
                            </Reveal>
                        )}
                        <Reveal delay={0.05}>
                            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/85">{section.secondBlock.copy}</p>
                        </Reveal>
                        {section.secondBlock.image && (
                            <div className="mt-10">
                                <ProjectImage src={section.secondBlock.image} caption={section.secondBlock.caption} />
                            </div>
                        )}
                    </div>
                )}
            </SectionWrapper>
        );
    }

    if (type === "images") {
        return (
            <SectionWrapper decorated={decorated} flip={flip} testid={testid}>
                <SectionHeading eyebrow={eyebrow}>{heading}</SectionHeading>
                <div className={`mt-12 ${section.images.length > 1 ? "grid grid-cols-1 gap-10 md:grid-cols-2" : "space-y-12"}`}>
                    {section.images.length > 0 ? (
                        section.images.map((img, i) =>
                            section.boxed ? (
                                <Reveal key={img.src} delay={(i % 2) * 0.08}>
                                    <figure>
                                        <div className="group overflow-hidden rounded-[2rem] bg-white p-5 ring-1 ring-ink/5 shadow-[0_20px_55px_rgba(112,71,245,0.12)] sm:p-7">
                                            <img
                                                src={img.src}
                                                alt={img.caption || heading}
                                                loading="lazy"
                                                className="h-auto w-full rounded-xl transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                                            />
                                        </div>
                                        {img.caption && (
                                            <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mist/80">
                                                {img.caption}
                                            </figcaption>
                                        )}
                                    </figure>
                                </Reveal>
                            ) : (
                                <ProjectImage key={img.src} src={img.src} caption={img.caption} delay={(i % 2) * 0.08} />
                            )
                        )
                    ) : (
                        <>
                            <Reveal><ImagePlaceholder label={heading} note={`${heading.toLowerCase()} coming soon ✦`} className="h-80 sm:h-[26rem]" /></Reveal>
                            <Reveal delay={0.08}><ImagePlaceholder label={heading} className="h-80 sm:h-[26rem]" /></Reveal>
                        </>
                    )}
                </div>
            </SectionWrapper>
        );
    }

    if (type === "styleguide") {
        return (
            <SectionWrapper decorated={decorated} flip={flip} testid={testid}>
                <SectionHeading eyebrow={eyebrow}>{heading}</SectionHeading>
                <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2">
                    {section.items.map((s, i) => (
                        <Reveal key={s.label} delay={(i % 2) * 0.08} className={s.fullWidth ? "sm:col-span-2" : ""}>
                            {s.image ? (
                                <figure>
                                    <div className="group overflow-hidden rounded-[2rem] ring-1 ring-ink/5 shadow-[0_24px_70px_rgba(112,71,245,0.15)]">
                                        <img
                                            src={s.image}
                                            alt={s.label}
                                            loading="lazy"
                                            className="h-auto w-full transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                                        />
                                    </div>
                                    <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mist/80">{s.label}</figcaption>
                                    {s.description && (
                                        <p className="mt-4 text-center text-sm leading-relaxed text-ink/70 max-w-2xl mx-auto">{s.description}</p>
                                    )}
                                </figure>
                            ) : (
                                <ImagePlaceholder label={s.label} className="h-72" />
                            )}
                        </Reveal>
                    ))}
                </div>
            </SectionWrapper>
        );
    }

    if (type === "moodboard") {
        return (
            <SectionWrapper decorated={decorated} flip={flip} testid={testid}>
                <SectionHeading eyebrow={eyebrow}>{heading}</SectionHeading>
                <div className="mt-12">
                    {section.image ? (
                        <Reveal>
                            <div className="group overflow-hidden rounded-[2rem] ring-1 ring-ink/5 shadow-[0_24px_70px_rgba(112,71,245,0.15)]">
                                <img
                                    src={section.image}
                                    alt={heading}
                                    loading="lazy"
                                    className="h-auto w-full transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                                />
                            </div>
                        </Reveal>
                    ) : (
                        <Reveal><ImagePlaceholder label={heading} note="moodboard coming soon ✦" className="h-96" /></Reveal>
                    )}
                    {section.description && (
                        <Reveal delay={0.1}>
                            <p className="mt-8 max-w-3xl text-lg leading-relaxed text-ink/85">{section.description}</p>
                        </Reveal>
                    )}
                </div>
            </SectionWrapper>
        );
    }

    if (type === "gallery") {
        return (
            <SectionWrapper decorated={decorated} flip={flip} testid={testid}>
                <SectionHeading eyebrow={eyebrow}>{heading}</SectionHeading>
                <div className="mt-14 space-y-20 lg:space-y-28">
                    {section.images.length > 0 ? (
                        section.images.map((img) => <ProjectImage key={img.src} src={img.src} caption={img.caption} delay={0.05} />)
                    ) : (
                        <>
                            <Reveal><ImagePlaceholder label={heading} note="final visuals coming soon ✦" className="h-96" /></Reveal>
                            <Reveal delay={0.08}><ImagePlaceholder label={heading} className="h-96" /></Reveal>
                        </>
                    )}
                </div>
            </SectionWrapper>
        );
    }

    return null;
};

export default function ProjectPage() {
    const { slug } = useParams();
    const index = projects.findIndex((p) => p.slug === slug);
    const project = projects[index];

    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [slug]);

    if (!project || !project.page) return <Navigate to="/" replace />;

    const { page } = project;
    const prev = projects[(index - 1 + projects.length) % projects.length];
    const next = projects[(index + 1) % projects.length];

    const NavCard = ({ target, dir }) => {
        const isPrev = dir === "prev";
        const inner = (
            <>
                <p className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-mist ${isPrev ? "" : "justify-end"}`}>
                    {isPrev && <ArrowLeft className="h-3.5 w-3.5 text-viol transition-transform duration-300 group-hover:-translate-x-1" />}
                    {isPrev ? "Previous Project" : "Next Project"}
                    {!isPrev && <ArrowRight className="h-3.5 w-3.5 text-viol transition-transform duration-300 group-hover:translate-x-1" />}
                </p>
                <p className={`mt-2 text-lg font-extrabold text-ink sm:text-xl ${isPrev ? "" : "text-right"}`}>{target.title}</p>
            </>
        );
        const cls = "glass group block rounded-[2rem] p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(112,71,245,0.2)]";
        return target.page ? (
            <Link to={`/work/${target.slug}`} className={cls} data-testid={`project-nav-${dir}`}>{inner}</Link>
        ) : (
            <button
                onClick={() => toast.info(`${target.title} — project page coming soon`)}
                className={`${cls} w-full text-left`}
                data-testid={`project-nav-${dir}`}
            >
                {inner}
            </button>
        );
    };

    return (
        <div data-testid="project-page">
            <main className="relative overflow-hidden pt-28 lg:pt-32">
                <div className="pointer-events-none absolute -top-40 -left-48 h-[32rem] w-[32rem] rounded-full bg-lav-200 opacity-80 blur-3xl" />
                <div className="pointer-events-none absolute top-1/4 -right-48 h-[28rem] w-[28rem] rounded-full bg-viol/10 blur-3xl" />

                <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
                    <Reveal>
                        <div className="overflow-hidden rounded-[2rem] ring-1 ring-ink/5 shadow-[0_30px_80px_rgba(112,71,245,0.2)] sm:rounded-[2.5rem]">
                            <img
                                src={page.banner}
                                alt={`${page.heading} — project banner`}
                                className="h-auto w-full"
                                data-testid="project-banner"
                            />
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <h1 className="mt-12 text-3xl font-extrabold tracking-tight text-ink sm:text-5xl" data-testid="project-title">
                            {page.heading}
                        </h1>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <dl className="mt-10 grid grid-cols-1 gap-x-8 gap-y-6 border-y border-lav-300/60 py-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-meta">
                            {page.meta.map((m) => (
                                <div key={m.label} className={["Deliverables", "Type"].includes(m.label) ? "sm:col-span-2 lg:col-span-1" : ""}>
                                    <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">{m.label}</dt>
                                    <dd className="mt-1.5 text-sm font-bold text-ink">{m.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>

                    {page.liveUrl && (
                        <Reveal delay={0.2}>
                            <div className="mt-7">
                                <a
                                    href={page.liveUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full border border-viol/25 bg-white/70 px-6 py-3 text-sm font-semibold text-viol backdrop-blur-sm transition-all duration-300 hover:border-viol hover:bg-viol hover:text-white hover:shadow-[0_8px_30px_rgba(112,71,245,0.25)] active:scale-95"
                                    data-testid="project-live-link"
                                >
                                    View Live Website
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                            </div>
                        </Reveal>
                    )}
                </div>

                {page.sections.map((section, i) => (
                    <SectionRenderer key={section.id} section={section} index={i} />
                ))}

                <section className="relative mx-auto max-w-6xl px-4 pb-24 sm:px-8" data-testid="project-navigation">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <NavCard target={prev} dir="prev" />
                        <NavCard target={next} dir="next" />
                    </div>
                </section>

                <Contact />
            </main>
            <Footer />
        </div>
    );
}
